import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, finalize, map, Observable, of, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BoardCompanyService } from '../../../../services/board-company.service';
import { DialogService } from '../../../../services/dialog.service';
import { NotificationService } from '../../../../services/notifications.service';
import { BoardCompany } from '../../board-v3.types';
import { formatContactPhone } from '../../contacts/contact.utils';
import { getCompanyEmails, getCompanyErrorKey, getCompanyName, getCompanyPhones } from '../company.utils';
import {
  CompanyPickerSlot,
  createEmptyCompanySlot,
  getCompanyDraftPayload,
} from '../company-picker/company-picker.component';
import { AddressInput } from '../../../../interfaces/card';
import { formatAddress, getAddressMapUrl } from '../../address.utils';

/** Company block of the card detail: a card has one company, it is picked, created or replaced in place */
@Component({
  selector: 'app-board-v3-card-company',
  templateUrl: './card-company.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3CardCompanyComponent {
  @Input({ required: true }) cardId!: string;
  @Input() company: BoardCompany | null = null;
  @Output() companyChange = new EventEmitter<BoardCompany | null>();

  private readonly companyService = inject(BoardCompanyService);
  private readonly dialogService = inject(DialogService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected busy = false;
  /** Slot of the edit mode, `null` while viewing */
  protected slot: CompanyPickerSlot | null = null;

  protected readonly getCompanyName = getCompanyName;
  protected readonly getCompanyPhones = getCompanyPhones;
  protected readonly getCompanyEmails = getCompanyEmails;
  protected readonly formatContactPhone = formatContactPhone;
  protected readonly getAddressMapUrl = getAddressMapUrl;

  protected formatAddress(address: AddressInput): string {
    return formatAddress(address, String(this.translateService.instant('address_apartment_short')));
  }

  protected phoneHref(phone: string): string {
    return `tel:${phone.replace(/[^\d+]/g, '')}`;
  }

  protected startEdit(): void {
    if (this.busy || this.slot) return;

    // Collapsed to a row; the company form opens with its edit button
    this.slot = this.company ? { existing: this.company, draft: null } : createEmptyCompanySlot();
  }

  protected cancelEdit(): void {
    if (!this.busy) this.slot = null;
  }

  protected setSlot(slot: CompanyPickerSlot): void {
    if (this.slot) this.slot = slot;
  }

  protected save(): void {
    const slot = this.slot;
    if (!slot || this.busy) return;

    slot.draft?.markAllAsTouched();
    if (slot.draft?.invalid) return;

    this.busy = true;
    this.saveSlot(slot)
      .pipe(
        finalize(() => (this.busy = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (company) => {
          this.slot = null;
          this.companyChange.emit(company);
        },
        error: (error) => this.showError(error),
      });
  }

  protected openCompany(): void {
    const company = this.company;
    if (!company) return;

    this.companyService
      .openCompanyDetail(company.id, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.deleted) {
          this.companyChange.emit(null);
          return;
        }
        if (!result?.company) return;

        const stillLinked = result.company.cards?.some((card) => card.id === this.cardId) ?? true;
        this.companyChange.emit(stillLinked ? result.company : null);
      });
  }

  protected unlink(): void {
    const company = this.company;
    if (!company || this.busy) return;

    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_company_unlink_confirm', { name: getCompanyName(company) }),
        isTranslate: true,
      })
      .pipe(
        filter(Boolean),
        switchMap(() => {
          this.busy = true;
          return this.companyService.unlinkCard(company.id, this.cardId).pipe(finalize(() => (this.busy = false)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.companyChange.emit(null),
        error: (error) => this.showError(error),
      });
  }

  /**
   * Links the picked company (saving its edits first) or creates the new one: the backend puts it in place
   * of the current company of the card. An emptied slot unlinks the current company.
   */
  private saveSlot(slot: CompanyPickerSlot): Observable<BoardCompany | null> {
    const { existing, draft } = slot;
    const current = this.company;

    if (!existing && !draft) {
      return current ? this.companyService.unlinkCard(current.id, this.cardId).pipe(map(() => null)) : of(null);
    }

    if (!existing) {
      return this.companyService.createCompany({ ...getCompanyDraftPayload(draft!), card_ids: [this.cardId] });
    }

    const updated$ = draft?.dirty
      ? this.companyService.updateCompany(existing.id, getCompanyDraftPayload(draft))
      : of(existing);

    return existing.id === current?.id
      ? updated$
      : updated$.pipe(switchMap(() => this.companyService.linkCards(existing.id, [this.cardId])));
  }

  private showError(error: unknown): void {
    this.notificationService.showError('', String(this.translateService.instant(getCompanyErrorKey(error))));
  }
}
